/**
 * <SalesPageHeader salesScope={salesScope} title="Sales overview" testId="sales-hub-heading">{actions}</SalesPageHeader>
 *
 * The h1 and one period line for a sales view: "1–30 Oct · vs previous 30 days (31 Aug–29 Sep)".
 * The comparison range is named once (years are added when the two ranges fall in different years).
 * `children` (e.g. the Upload sales button) sit on the right and wrap under the title on phones.
 *
 * Also exports periodLine(salesScope) for tests / other headers, importMessage(summary) and
 * <ImportNotice message onDismiss /> (the dismissible "N sales records imported" status line).
 */
import React from 'react';
import { X } from 'lucide-react';
import { formatDateRange } from '../format';

export const periodLine = (salesScope) => {
  const { scope, comparison, compareLabel } = salesScope || {};
  if (!scope?.date_from || !scope?.date_to) return '';
  const prevFrom = comparison?.date_from;
  const prevTo = comparison?.date_to;
  const withYear = Boolean(prevFrom && prevTo)
    && (scope.date_from.slice(0, 4) !== prevFrom.slice(0, 4) || scope.date_to.slice(0, 4) !== prevTo.slice(0, 4));
  const current = formatDateRange(scope.date_from, scope.date_to, withYear);
  const previous = prevFrom && prevTo ? ` (${formatDateRange(prevFrom, prevTo, withYear)})` : '';
  return `${current} · ${compareLabel || 'vs previous period'}${previous}`;
};

export const SalesPageHeader = ({ salesScope, title, testId, children }) => (
  <header className="flex flex-wrap items-start justify-between gap-3">
    <div className="min-w-0">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl" data-testid={testId}>{title}</h1>
      <p className="mt-1 text-sm text-gray-500" data-testid="sales-report-period">{periodLine(salesScope)}</p>
    </div>
    {children}
  </header>
);

export default SalesPageHeader;

/** Status text after a sales upload (shared by the overview and the SKU / brand / platform views). */
export const importMessage = (summary = {}) => {
  if (summary.already_imported) return 'This file was already imported. No duplicate records were added.';
  const count = value => Number(value || 0).toLocaleString('en-IN');
  const parts = [`${count(summary.imported_count)} sales records imported.`];
  if (summary.skipped_overlap_count) parts.push(`${count(summary.skipped_overlap_count)} overlapping row(s) skipped to avoid double counting.`);
  if (summary.skipped_unmatched_count) parts.push(`${count(summary.skipped_unmatched_count)} unmatched row(s) skipped.`);
  if (summary.saved_mappings) parts.push(`${count(summary.saved_mappings)} new code mapping(s) saved.`);
  return parts.join(' ');
};

/** Green status line after an upload, with a dismiss button. Renders nothing without a message. */
export const ImportNotice = ({ message, onDismiss }) => (message ? (
  <div role="status" className="flex items-start justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
    <p data-testid="sales-import-success">{message}</p>
    <button type="button" onClick={onDismiss} aria-label="Dismiss import message" data-testid="sales-import-success-dismiss"
      className="shrink-0 rounded p-0.5 text-green-700 hover:bg-green-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600">
      <X className="h-4 w-4" aria-hidden="true" />
    </button>
  </div>
) : null);
